import React, { useState, useEffect } from "react";
import { useNavigate } from "../i18n/router";
import { useAuth } from "../contexts/AuthContext";
import { BarChart3, Users, Calendar, MessageCircle, Star, UserPlus, Loader2, TrendingUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MetricsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login?redirect=/admin/metrics");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/metrics`, { credentials: "include" });
        if (res.ok) setMetrics(await res.json());
        else if (res.status === 403) navigate("/");
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [user, navigate]);

  if (authLoading || !user || loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" style={{ color: "var(--eunoia-accent)" }} /></div>;
  }
  if (!metrics) return null;

  const cards = [
    { label: "Пользователи", value: metrics.totalUsers, sub: `+${metrics.newUsersWeek} за неделю`, icon: Users, color: "#BDA27A" },
    { label: "Новые (месяц)", value: metrics.newUsersMonth, sub: "за 30 дней", icon: TrendingUp, color: "#16a34a" },
    { label: "События", value: metrics.totalEvents, icon: Calendar, color: "#0ea5e9" },
    { label: "Организаторы", value: metrics.totalOrganizers, icon: UserPlus, color: "#8b5cf6" },
    { label: "Сообщения", value: metrics.totalMessages, sub: `+${metrics.messagesWeek} за неделю`, icon: MessageCircle, color: "#f59e0b" },
    { label: "Отзывы", value: metrics.totalReviews, icon: Star, color: "#ef4444" },
    { label: "Подписки", value: metrics.totalFollows, icon: UserPlus, color: "#06b6d4" },
  ];

  return (
    <div className="min-h-screen eunoia-space-lg px-4 pb-16" data-testid="metrics-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <BarChart3 className="h-7 w-7" style={{ color: "var(--eunoia-accent)" }} />
          <h1 className="eunoia-title-large">Метрики платформы</h1>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-10">
          {cards.map((c, i) => (
            <div key={i} className="eunoia-surface p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${c.color}15` }}>
                  <c.icon className="h-4 w-4" style={{ color: c.color }} />
                </div>
                <span className="text-xs font-medium" style={{ color: "var(--eunoia-soft)" }}>{c.label}</span>
              </div>
              <p className="text-2xl font-bold" style={{ color: "var(--eunoia-deep)" }}>{c.value}</p>
              {c.sub && <p className="text-xs mt-1" style={{ color: "var(--eunoia-soft)" }}>{c.sub}</p>}
            </div>
          ))}
        </div>

        {/* Top Organizers */}
        {metrics.topOrganizers && metrics.topOrganizers.length > 0 && (
          <div className="eunoia-surface p-6">
            <h2 className="eunoia-title-small mb-4 flex items-center gap-2">
              <Star className="h-5 w-5" style={{ color: "var(--eunoia-accent)" }} />
              Топ организаторов по подписчикам
            </h2>
            <div className="space-y-3">
              {metrics.topOrganizers.map((org, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-white/40">
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold"
                      style={{ backgroundColor: "var(--eunoia-accent-whisper)", color: "var(--eunoia-accent)" }}>{i + 1}</span>
                    <div>
                      <p className="text-sm font-semibold" style={{ color: "var(--eunoia-deep)" }}>{org.name}</p>
                      <p className="text-xs" style={{ color: "var(--eunoia-soft)" }}>{org.email}</p>
                    </div>
                  </div>
                  <span className="text-sm font-bold" style={{ color: "var(--eunoia-accent)" }}>{org.followers} подп.</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MetricsPage;
